import React, { useMemo, useState } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../ui/button';
import { Calendar, ChevronLeft, ChevronRight, MapPin, User } from 'lucide-react';

const COLORS = [
  { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-900', dot: 'bg-cyan-500' },
  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-900', dot: 'bg-violet-500' },
  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', dot: 'bg-amber-500' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', dot: 'bg-emerald-500' },
  { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-900', dot: 'bg-rose-500' },
  { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-900', dot: 'bg-sky-500' },
];
const DEMI = { matin: 'Matin', apres_midi: 'Après-midi' };

// Planning hebdomadaire (lecture seule) des actions de formation visibles sur le portail entreprise / financeur
export const PlanningHebdoEntreprise = ({ actions }) => {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const byDate = useMemo(() => {
    const map = {};
    actions.forEach((a, idx) => (a.planning || []).forEach(p => {
      (map[p.date] = map[p.date] || []).push({ ...p, action: a, color: COLORS[idx % COLORS.length] });
    }));
    return map;
  }, [actions]);
  const nbAf = new Set(days.flatMap(d => (byDate[format(d, 'yyyy-MM-dd')] || []).map(e => e.action.dossier_id))).size;

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm mb-8" data-testid="planning-hebdo-entreprise">
      <div className="flex items-center justify-between gap-3 flex-wrap border-b bg-slate-50/60 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cyan-600" /> Planning hebdomadaire de vos formations
          {nbAf > 0 && <span className="text-xs font-normal text-slate-400">({nbAf} AF)</span>}
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setWeekStart(subWeeks(weekStart, 1))} data-testid="hebdo-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
          <span className="text-xs font-medium sm:min-w-[170px] text-center whitespace-nowrap" data-testid="hebdo-range">{format(weekStart, 'd MMM', { locale: fr })} – {format(addDays(weekStart, 6), 'd MMM yyyy', { locale: fr })}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="hebdo-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="hebdo-today">Aujourd'hui</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
      <div className="min-w-[560px]">
      <div className="grid grid-cols-7 border-b bg-slate-50">
        {days.map(day => {
          const today = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} className={`p-2 text-center border-r last:border-r-0 ${today ? 'bg-cyan-50' : ''}`}>
              <p className="text-[10px] font-medium text-slate-400 uppercase">{format(day, 'EEE', { locale: fr })}</p>
              <p className={`text-lg font-semibold ${today ? 'text-cyan-700' : 'text-slate-900'}`}>{format(day, 'd')}</p>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-7 min-h-[150px]" data-testid="hebdo-grid">
        {days.map(day => {
          const key = format(day, 'yyyy-MM-dd');
          const entries = byDate[key] || [];
          const today = isSameDay(day, new Date());
          return (
            <div key={key} className={`border-r last:border-r-0 p-1.5 min-h-[150px] ${today ? 'bg-cyan-50/30' : ''}`} data-testid={`hebdo-day-${key}`}>
              {entries.length === 0 ? (
                <div className="h-full flex items-center justify-center"><span className="text-xs text-slate-300">-</span></div>
              ) : (
                <div className="space-y-1">
                  {entries.map((e, i) => (
                    <div key={`${e.action.dossier_id}-${i}`} className={`p-1.5 rounded-md border ${e.color.bg} ${e.color.border}`} title={`${e.action.code} — ${e.action.intitule}`} data-testid={`hebdo-entry-${e.action.dossier_id}-${key}`}>
                      <p className={`text-[10px] font-semibold ${e.color.text} truncate leading-tight`}>{e.action.intitule}</p>
                      <p className="text-[9px] text-slate-500 font-mono">{e.action.code}{e.type_journee === 'stage' ? ' · Stage' : DEMI[e.demi_journee] ? ` · ${DEMI[e.demi_journee]}` : ''}</p>
                      {e.lieu && <p className="text-[9px] text-slate-500 flex items-center gap-0.5 truncate"><MapPin className="w-2.5 h-2.5 shrink-0" /> {e.lieu}</p>}
                      {e.formateur && <p className="text-[9px] text-slate-500 flex items-center gap-0.5 truncate"><User className="w-2.5 h-2.5 shrink-0" /> {e.formateur}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
      </div>
    </section>
  );
};
